import { Component, EventEmitter, Input, Output } from '@angular/core';

/* =============================================================================
   pla-classification-pill — the shared PlateX classification pill
   (Dateless build pack §8). NOT a Dateless-specific control: the same
   component serves Plate Detail, the Dateless Reference, Search Results,
   Collections and future reference pages.

   Modes:  link (routerLink)  ·  button (emits selected)  ·  static (span)
   Sizes:  sm (contextual, plate detail — never shows a count)
           md (search / collection / filter surfaces)
           lg (senior — the Dateless browse row)

   Deep-link contract: a format pill's link target is the Dateless Reference
   with its token, `/plates/dateless/<token>` (during Phases 2–7 the draft
   route `/dateless-draft/<token>`); the token is always `letters × numbers`.

   Counts: `count` accepts ONLY a genuine current-inventory figure. Never pass
   a verified-sale sample size or any historic figure — a number in this
   position reads as "available now" (§8). Omit it and the pill renders
   code-only.

   Accessibility: never expose only the visual token. `describe` carries the
   plain-English definition ("Two letters and one number") and is announced
   via aria-label in link/button modes.
   ========================================================================== */

export type PillVariant = 'format' | 'category' | 'arrangement' | 'letter';
export type PillSize = 'sm' | 'md' | 'lg';
export type PillMode = 'link' | 'button' | 'static';

@Component({
  selector: 'pla-classification-pill',
  templateUrl: './classification-pill.component.html',
  styleUrls: ['./classification-pill.component.scss'],
})
export class ClassificationPillComponent {
  /** Primary identity, e.g. "2×1", "Dateless", "N LL". */
  @Input() label = '';
  /** Plain-English definition for assistive technology and tooltips. */
  @Input() describe = '';
  /** Genuine current-inventory count ONLY (never historic figures). */
  @Input() count: number | null = null;
  @Input() variant: PillVariant = 'format';
  @Input() size: PillSize = 'md';
  @Input() active = false;
  @Input() disabled = false;
  /** routerLink target; providing it selects link mode unless `as` overrides. */
  @Input() href: string | any[] | null = null;
  @Input() as: PillMode | null = null;

  @Output() selected = new EventEmitter<void>();

  public get mode(): PillMode {
    if (this.as) return this.as;
    return this.href ? 'link' : 'static';
  }

  /** sm pills never carry a count (§8); md/lg only when a live count exists. */
  public get showCount(): boolean {
    return this.size !== 'sm' && this.count !== null && this.count !== undefined;
  }

  public get ariaLabel(): string {
    const base = this.describe ? `${this.label}: ${this.describe}` : this.label;
    return this.variant === 'format' ? `Browse ${base}` : base;
  }

  public onSelect(ev: Event): void {
    ev.preventDefault();
    if (!this.disabled) this.selected.emit();
  }
}
